<template>
  <content-wrapper class="list-wrapper" :style="finalStyle" :empty-props="{
    icon: 'goods',
    title: $t(`message.goodsEmptyTitle`),
    desc: $t('message.goodsEmptyDesc'),
  }" v-model:error="pageError" :empty="loaded && empty" :loading="loading" @reload="refreshData">
    <van-list class="goods-list" v-model:loading="loading" :finished="pageError || !hasMore"
      :finished-text="loaded && records.length > 4 ? $t('message.noMoreGoods') : ''" @load="onLoad">
      <template #loading>
        <van-loading size="24px" />
      </template>
      <div class="container">
        <goods-card v-for="(item, index) in records" :item="item" :index="index" />
      </div>
    </van-list>
  </content-wrapper>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { getCache, setCache } from '@voghion/native-api'
import { getCategoryGoodsList, getPopularGoodsList } from '@/api/content'
import GoodsCard from './GoodsCard.vue'
import ContentWrapper from '@/components/ContentWrapper.vue'
import { useEnvStore } from '@/states/env'
import { storeToRefs } from 'pinia'

const store = useEnvStore()
const { pagePaddingTop } = storeToRefs(store)

const props = defineProps({
  /**
   * 分类id
   */
  value: {
    type: String,
    required: true,
    default () {
      return ''
    }
  }
})

const finalStyle = computed(() => {
  return {
    minHeight: `calc(100vh - ${pagePaddingTop.value + 44}px)`,
  }
})

const POPULAR_VALUE = '26'

const isPopular = computed(() => {
  return props.value === POPULAR_VALUE
})

const pageError = ref(null)
const loading = ref(false)
const records = ref([])
const loaded = ref(false)
const hasMore = ref(false)
const pageNow = ref(0)
const pageSize = ref(20)
const cacheKey = computed(() => {
  return `goodsList_${props.value}`
})
const emptyCount = ref(0)
const empty = computed(() => {
  return records.value.length === 0 && emptyCount.value >= 3
})
const refreshData = async () => {
  const res = await getCache({
    key: cacheKey.value
  })
  if (!res.error && res.data.value) {
    const data = JSON.parse(res.data.value)
    records.value = data.records
    hasMore.value = data.hasMore
    pageNow.value = data.pageNow
    pageSize.value = data.pageSize
    loaded.value = true
  } else {
    hasMore.value = true
  }
}
const onLoad = () => {
  loading.value = true
  const currentPage = pageNow.value + 1
  const p = isPopular.value ? getPopularGoodsList({
    source: 3,
    pageNow: currentPage,
    pageSize: pageSize.value
  }) : getCategoryGoodsList({
    categoryId: props.value,
    modelType: '22',
    orderBy: 8,
    pageNow: currentPage,
    pageSize: pageSize.value
  })
  p.then((res) => {
    loading.value = false
    if (res.rtn_code === '0000') {
      loaded.value = true
      const data = res.data
      const moreRecords = data.records || []
      records.value = records.value.concat(moreRecords)
      if (!moreRecords.length) {
        emptyCount.value++
      } else {
        emptyCount.value = 0
      }
      hasMore.value = emptyCount.value < 3 || moreRecords.length > 0
      pageNow.value = data.pageNow
      pageSize.value = data.pageSize
      setCache({
        key: cacheKey.value,
        value: JSON.stringify({
          hasMore: hasMore.value,
          records: records.value,
          pageNow: data.pageNow,
          pageSize: data.pageSize
        })
      })
    }
  }, (err) => {
    if (currentPage === 1) {
      pageError.value = err
    }
    loading.value = false
  })
}
onMounted(() => {
  refreshData()
})
</script>

<style lang="scss" scoped>
.list-wrapper {
  background: #f8f8f8;
  min-height: calc(100vh - 44px);

  :deep(.empty-wrapper) {
    padding-top: 100px;
  }
}

.goods-list {
  box-sizing: border-box;
  width: 100%;
  padding: 10px;

  .loading-area {
    text-align: center;
    padding: 100px 0;
    background: transparent;
  }

  .container {
    display: grid;
    grid-auto-rows: 5Px;
    grid-template-columns: repeat(auto-fill, calc(50% - 5Px));
    align-items: start;
    justify-content: space-between;
  }
}
</style>
@/api